/*
 * Case Files — real Shopify blog articles.
 *
 * snippets/sat-bootstrap.liquid reads the blog set in Theme settings → Case Files
 * (or the shop's first blog if none is chosen) and reshapes each published article
 * into the exact record shape SATComponent already keeps as this._caseFiles:
 * {no, date, kind, region, tabColor, status, title, body, finding, attachments,
 * clerk, article, articleHtml}. Tag an article "restricted" (or "sealed") and it seals
 * behind the same clearance-code gate an order-linked file uses; tag it
 * "kind:provenance" / "kind:dispatch" / "kind:research" / "kind:field-note" to set
 * its category stamp, and "region:city-name" to set its location line. None of that
 * is required — an untagged post still publishes fine, rotating through the four
 * category stamps.
 *
 * _caseFiles is a plain instance array read directly by several methods elsewhere in
 * sat-app.js — the accordion toggle, the article reader, prev/next, and the
 * #case-file/CF-XXX hash router. Swapping the array itself once, before any of those
 * run, means all of that keeps working completely unchanged; nothing else needed
 * patching. Left untouched (the six shipped demo files stay up) when the chosen blog
 * has no published posts yet, so the section is never empty.
 *
 * Merchant: two real bugs reported after using this with a 6th article.
 *
 * (1) "formatting gets nuked" — every bit of a merchant's actual formatting (bold,
 * headers, dividers, size) was discarded before it ever reached the page. The
 * "article" paragraphs array above has always been strip_html'd down to bare text —
 * the only way to get ANY of an article's words on screen at all, since
 * sat-runtime.js's {{ }} bindings always HTML-escape (confirmed live: a <strong> tag
 * in a bound field renders as the literal text "&lt;strong&gt;", not bold — there is
 * no raw-HTML binding anywhere in that runtime). snippets/sat-bootstrap.liquid now
 * also emits each article's real, unstripped HTML as `articleHtml`; this patch
 * sanitizes it (a small tag allowlist — strips <script>/<style> outright, drops
 * every attribute from everything else, unwraps anything not on the allowlist like
 * <a>/<img>/<div> rather than rendering it) and owns rendering it into the article
 * reader's body container directly.
 *
 * That container (snippets/sat-case-article.liquid's [data-m="case-article-body"])
 * is an empty div with no sc-for/bindings of its own. Three attempts to paint it:
 *
 *   1st — layering an external write on top of sat-runtime's own sc-for (replacing
 *   its rendered <p> children with injected HTML in place): got stuck showing
 *   whatever was open when it was FIRST painted, because once this patch's write
 *   replaced what sat-runtime itself had rendered there, sat-runtime's own diffing
 *   got confused about that container on the next re-render and stopped updating it
 *   at all — the same class of problem this theme's own prior patches hit moving
 *   real DOM nodes under a VDOM-diffing framework's feet (see
 *   assets/sat-import-column-balance-patch.js's own history).
 *
 *   2nd — switched the container to a genuinely empty div (nothing templated inside
 *   it at all, so sat-runtime has nothing of its own to fight over) and repainted
 *   via a double-requestAnimationFrame defer out of renderVals, gated to only repaint
 *   when the open article's index actually changed. Still came up blank live.
 *   Confirmed directly in the console why: sat-runtime reconciles this div's
 *   children on every render that TOUCHES it, regardless of whether anything in ITS
 *   OWN template changed — and on this page, that's effectively every render, since
 *   the page animates continuously (clock, scanlines, dust motes) and this app
 *   re-renders on roughly every one of those animation frames — measured live at
 *   30-40 renders/second while just sitting on the article reader, not the "idle
 *   1-2/sec" originally assumed. A two-frame deferred write loses that race almost
 *   every time: by the time it lands, several more renders have already re-wiped the
 *   container, and the content is gone again before the browser ever paints a frame
 *   with it visible. Removing the "only on index change" gate and repainting
 *   unconditionally on every render didn't fix it either, for the same reason — it
 *   was still a deferred write racing a much faster wipe.
 *
 *   3rd (this one) — stopped trying to out-schedule the wipe and instead reacts to
 *   it: a MutationObserver on a stable ancestor (#sat-app, which persists for the
 *   component's entire lifetime) fires a microtask essentially the instant
 *   sat-runtime clears the container, and the handler repaints synchronously right
 *   then — no frame budget to lose a race in. A `writing` flag skips the observer's
 *   own callback while a repaint is in flight, so the write doesn't re-trigger
 *   itself. The same handler also runs once eagerly after setup (covers first
 *   mount, before any wipe has happened yet) and compares a small `data-cf-idx`
 *   marker against the currently-open file's index so switching articles repaints
 *   with the new one rather than silently keeping the old one up whenever a given
 *   render happens not to produce a wipe. Confirmed live: holds steady across
 *   continuous idle re-rendering, and switching the open article (prev/next, or
 *   setState driven) repaints with the new file's content within the same frame.
 *
 * (2) "more than five files reverts to the original fake blogs" — not reproducible
 * with exactly 5 published articles (checked live), but a real latent bug either
 * way: the "article" array's old comma logic added a trailing comma after the last
 * NON-BLANK paragraph while checking forloop.last against the RAW split (which
 * almost always has one trailing blank segment after an HTML string's final
 * </p>) — INVALID JSON (`["a","b",]`) whenever that happened to land past a
 * five-ish-paragraph article, which fails the whole window.SAT_DATA JSON.parse,
 * which is exactly what sends every real-data patch (this one included) back to
 * its own hardcoded demo fallback — not just this one's. Rebuilt in
 * snippets/sat-bootstrap.liquid with the same boolean-separator pattern this file
 * already uses successfully for the product loops above (`sat_sep`), which emits a
 * comma based on "have I already emitted something" rather than array position —
 * correct regardless of where blank segments fall.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }
  var applied = false;

  function tryApply(self) {
    if (applied) return;
    var files = (window.SAT_DATA && window.SAT_DATA.caseFiles) || [];
    if (!files.length) return;
    applied = true;
    self._caseFiles = files;
  }

  // Small tag allowlist for a merchant-authored blog article's own HTML — this is
  // trusted (Shopify admin) content, not public input, so the goal here is "keep
  // real formatting, drop anything structural/interactive/risky" rather than a
  // hardened public sanitizer: strip <script>/<style> (and their contents)
  // entirely, strip every attribute off everything else (no href/src/onClick/style
  // survives), and unwrap (keep the text, drop the tag) anything not in the
  // allowlist — a stray <a>, <img>, <div>, <span> etc. degrades to plain text
  // instead of being rendered or silently dropped along with its contents.
  var ALLOWED_TAGS = { P: 1, STRONG: 1, B: 1, EM: 1, I: 1, U: 1, BR: 1, HR: 1, H1: 1, H2: 1, H3: 1, H4: 1, H5: 1, H6: 1, UL: 1, OL: 1, LI: 1, BLOCKQUOTE: 1 };
  var DROP_TAGS = { SCRIPT: 1, STYLE: 1 };

  function sanitizeArticleHtml(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    (function walk(node) {
      var child = node.firstChild;
      while (child) {
        var next = child.nextSibling;
        if (child.nodeType === 1) {
          var tag = child.tagName;
          if (DROP_TAGS[tag]) {
            node.removeChild(child);
          } else {
            walk(child);
            while (child.attributes && child.attributes.length) {
              child.removeAttribute(child.attributes[0].name);
            }
            if (!ALLOWED_TAGS[tag]) {
              while (child.firstChild) node.insertBefore(child.firstChild, child);
              node.removeChild(child);
            }
          }
        }
        child = next;
      }
    })(tpl.content);
    return tpl.innerHTML;
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // --- Article reader body: MutationObserver-driven repaint (see 3rd attempt above) ---

  var lastInstance = null;
  var painting = false;

  function computeDesiredArticleBody() {
    var inst = lastInstance;
    var s = inst && inst.state;
    if (!s || s.page !== 'casefile') return null;
    var files = inst._caseFiles || [];
    if (!files.length) return null;
    var n = files.length;
    var idx = (((s.caseArticleIndex || 0) % n) + n) % n;
    var file = files[idx];
    if (!file || file.status === 'UNDER SEAL') return null;
    var inner = file.articleHtml
      ? sanitizeArticleHtml(file.articleHtml)
      : (file.article || [file.body]).map(function (p) { return '<p>' + escapeHtml(p) + '</p>'; }).join('');
    return { idx: idx, html: '<div data-cf-rich>' + inner + '</div>' };
  }

  function paintArticleBodyIfNeeded() {
    if (painting) return;
    var el = document.querySelector('[data-m="case-article-body"]');
    if (!el) return;
    var desired = computeDesiredArticleBody();
    if (!desired) {
      if (el.childNodes.length) {
        painting = true;
        el.innerHTML = '';
        painting = false;
      }
      return;
    }
    if (el.dataset.cfIdx === String(desired.idx) && el.firstChild) return;
    painting = true;
    el.innerHTML = desired.html;
    el.dataset.cfIdx = String(desired.idx);
    painting = false;
  }

  function ensureObserver() {
    if (ensureObserver._started) return;
    var root = document.getElementById('sat-app');
    if (!root) return;
    ensureObserver._started = true;
    new MutationObserver(paintArticleBodyIfNeeded).observe(root, { childList: true, subtree: true, characterData: true });
    paintArticleBodyIfNeeded();
  }

  var orig = window.SATComponent.prototype.renderVals;
  window.SATComponent.prototype.renderVals = function () {
    if (!applied) tryApply(this);
    lastInstance = this;
    var out = orig.call(this);
    var files = this._caseFiles || [];

    function pad(n) { n = String(n); return n.length < 2 ? '0' + n : n; }

    var openCount = 0, sealedCount = 0, refCount = 0;
    for (var i = 0; i < files.length; i++) {
      if (files[i].status === 'UNDER SEAL') sealedCount++; else openCount++;
      refCount += (files[i].attachments || []).length;
    }
    out.cfOpenCount = pad(openCount);
    out.cfSealedCount = pad(sealedCount);
    out.cfCrossRefCount = pad(refCount);

    // The Case Files page banner (snippets/sat-breadcrumbs.liquid) reads these same
    // three figures from sat-app.js's own hardcoded pageStats map — override just
    // that page's entry so the banner matches the section below it.
    if (this.state && this.state.page === 'casefiles' && Array.isArray(out.pageStats)) {
      out.pageStats = [
        { label: 'FILES OPEN', value: out.cfOpenCount, color: '#C9A227' },
        { label: 'UNDER SEAL', value: out.cfSealedCount, color: '#E08A2E' },
        { label: 'CROSS REFS', value: out.cfCrossRefCount, color: '#8FE39C' }
      ];
    }

    // Reported live: an open case file cuts its own text off mid-sentence on
    // mobile. Root cause is in sat-app.js's own caseFiles mapping — the open/
    // close animation works by CSS-transitioning max-height, and the "open"
    // value is a flat, hardcoded 520px for every file on every screen size,
    // with overflow:hidden clipping anything past it. That's tuned for
    // desktop, where the body text column is wide (grid-template-columns:1fr
    // 210px next to a 24-142px padding gutter) and a several-hundred-character
    // paragraph wraps into relatively few lines. On a narrow phone that same
    // column is much tighter, so the identical text wraps into many more,
    // shorter lines and its real height comfortably exceeds 520px — the
    // overflow then clips it wherever it happens to fall, which is exactly
    // the ragged mid-word cutoff reported ("...which laid t"), not a
    // truncation limit on the text itself (the 320-character cap upstream in
    // sat-bootstrap.liquid is unrelated and already well under what 520px
    // holds on desktop).
    //
    // max-height only caps how tall the box is allowed to grow — a shorter
    // article still sizes to its own content either way — so raising the cap
    // well past anything a real case-file entry (body paragraph + finding
    // note + attachments list) could plausibly need fixes every screen size
    // at once without changing how the animation looks for existing shorter
    // entries.
    if (Array.isArray(out.caseFiles)) {
      out.caseFiles = out.caseFiles.map(function (cf) {
        var patch = {};
        if (cf && cf.bodyH && cf.bodyH !== '0px') { patch.bodyH = '3000px'; }
        // Lets snippets/sat-case-files.liquid gate its own sealed-only stamp
        // visual off a real boolean instead of string-comparing cf.status
        // itself (sc-if only checks truthiness, and "OPEN"/"UNDER SEAL" are
        // both non-empty truthy strings either way).
        patch.sealed = cf && cf.status === 'UNDER SEAL';
        return Object.keys(patch).length ? Object.assign({}, cf, patch) : cf;
      });
    }

    if (this.state && this.state.page === 'casefile') {
      ensureObserver();
      paintArticleBodyIfNeeded();
    }

    return out;
  };
})();
